import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Heart, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { copy } from '@/copy';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    const verify = async () => {
      try {
        await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [token]);

  const passwordChecks = [
    { label: copy.resetPassword.req8Chars, met: newPassword.length >= 8 },
    { label: copy.resetPassword.reqUppercase, met: /[A-Z]/.test(newPassword) },
    { label: copy.resetPassword.reqLowercase, met: /[a-z]/.test(newPassword) },
    { label: copy.resetPassword.reqNumber, met: /[0-9]/.test(newPassword) },
    { label: copy.resetPassword.reqSpecialChar, met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) },
  ];
  const allMet = passwordChecks.every(c => c.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allMet) {
      toast.error(copy.resetPassword.weakPassword);
      return;
    }
    if (!passwordsMatch) {
      toast.error(copy.resetPassword.mismatch);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccess(true);
      toast.success(copy.resetPassword.successToast);
    } catch (err) {
      toast.error(err.response?.data?.detail || copy.resetPassword.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="px-4 py-6 border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
          </Link>
          <Link to="/login" className="text-sm text-[#424E59] hover:text-[#1D2D44] transition-colors">
            {copy.recovery.backToLogin}
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-none shadow-lg rounded-2xl overflow-hidden" data-testid="reset-password-card">
          <div className="h-1 bg-[#B89B5E]"></div>
          <CardContent className="p-8">

            {!token || !tokenValid ? (
              <div className="text-center animate-fade-in" data-testid="token-invalid">
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-3">{copy.resetPassword.invalidTitle}</h2>
                <p className="text-[#3A4A5A] text-sm mb-6">{copy.resetPassword.invalidMessage}</p>
                <Link to="/recover">
                  <Button className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8" data-testid="try-again-btn">
                    {copy.resetPassword.tryAgain}
                  </Button>
                </Link>
              </div>
            ) : success ? (
              <div className="text-center animate-fade-in" data-testid="reset-success">
                <div className="w-12 h-12 bg-[#5E7F63]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-[#5E7F63]" />
                </div>
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-3">{copy.resetPassword.successTitle}</h2>
                <p className="text-[#3A4A5A] text-sm mb-6">{copy.resetPassword.successMessage}</p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8"
                  data-testid="go-to-login-btn"
                >
                  {copy.resetPassword.goToLogin}
                </Button>
              </div>
            ) : (
              <div className="animate-fade-in">
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-2">{copy.resetPassword.title}</h2>
                <p className="text-[#3A4A5A] text-sm mb-6">{copy.resetPassword.subtitle}</p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#1D2D44] mb-1.5">{copy.resetPassword.newPasswordLabel}</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={copy.resetPassword.newPasswordPlaceholder}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#E5E5E5] bg-white text-[#1D2D44] placeholder:text-[#B5ADA7] focus:outline-none focus:ring-2 focus:ring-[#B89B5E]/40 focus:border-[#B89B5E] transition-all pr-12"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#424E59] hover:text-[#1D2D44]"
                        data-testid="toggle-password-btn"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Password strength indicators */}
                  <div className="mb-5 space-y-1.5" data-testid="password-requirements">
                    {passwordChecks.map((check, i) => (
                      <div key={i} className={`flex items-center gap-2 text-xs transition-colors ${check.met ? 'text-[#5E7F63]' : 'text-[#B5ADA7]'}`}>
                        <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-all ${
                          check.met ? 'bg-[#5E7F63] border-[#5E7F63]' : 'border-[#D5CDC5]'
                        }`}>
                          {check.met && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        {check.label}
                      </div>
                    ))}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#1D2D44] mb-1.5">{copy.resetPassword.confirmLabel}</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={copy.resetPassword.confirmPlaceholder}
                      required
                      className={`w-full px-4 py-3 rounded-xl border bg-white text-[#1D2D44] placeholder:text-[#B5ADA7] focus:outline-none focus:ring-2 focus:ring-[#B89B5E]/40 transition-all ${
                        confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-[#E5E5E5] focus:border-[#B89B5E]'
                      }`}
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && !passwordsMatch && (
                      <p className="text-red-500 text-xs mt-1">{copy.resetPassword.mismatch}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !allMet || !passwordsMatch}
                    className="w-full bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full py-6 text-base font-medium transition-all disabled:opacity-50"
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
                        {copy.resetPassword.resetting}
                      </span>
                    ) : (
                      copy.resetPassword.submitButton
                    )}
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
